import { odooApi } from '../index';
import { getOdooLang } from '@/config/api.config';
import type { PaginatedResponse } from '@/types';
import { extractErrorMessage } from '../transforms';

export interface Assessment {
  id: string;
  name: string;
  description: string; // HTML from Odoo
  accessToken: string;
  testUrl: string | null;
  resultsUrl: string | null;
  image: string | null;
  createdAt: string | null;
}

interface OdooAssessment {
  id: number;
  name: string;
  description: string;
  access_token: string;
  test_url: string | null;
  results_url: string | null;
  image: string | null;
  create_date: string | null;
}

interface OdooAssessmentsResponse {
  success: boolean;
  data: OdooAssessment[];
  pagination?: {
    total_records: number;
    total_pages: number;
    current_page: number;
    page_size: number;
  };
  total_records?: number;
  language?: string;
}

function transformOdooAssessment(item: OdooAssessment): Assessment {
  return {
    id: String(item.id),
    name: item.name || '',
    description: item.description || '',
    accessToken: item.access_token || '',
    testUrl: item.test_url || null,
    resultsUrl: item.results_url || null,
    image: item.image || null,
    createdAt: item.create_date || null,
  };
}

export const assessmentsApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    getAssessments: builder.query<
      PaginatedResponse<Assessment>,
      { page?: number; pageSize?: number; lang?: 'ar' | 'en' | 'ku' }
    >({
      query: (
        { page = 1, pageSize = 12, lang = 'ar' }: { page?: number; pageSize?: number; lang?: 'ar' | 'en' | 'ku' } = {}
      ) => {
        const odooLang = getOdooLang(lang);
        return {
          url: `/api/moys/assessments?page=${page}&page_size=${pageSize}&lang=${odooLang}`,
          method: 'POST',
        };
      },
      transformResponse: (response: OdooAssessmentsResponse, meta, arg) => {
        const page = arg?.page ?? 1;
        const pageSize = arg?.pageSize ?? 12;

        if (!response || !Array.isArray(response.data)) {
          throw new Error('Invalid response format from assessments API');
        }

        const data = response.data.map(transformOdooAssessment);

        if (response.pagination) {
          const p = response.pagination;
          return {
            data,
            total: p.total_records,
            page: p.current_page,
            pageSize: p.page_size,
            totalPages: p.total_pages,
          } as PaginatedResponse<Assessment>;
        }

        const total = response.total_records ?? data.length;
        return {
          data,
          total,
          page,
          pageSize,
          totalPages: Math.ceil(total / pageSize),
        } as PaginatedResponse<Assessment>;
      },
      transformErrorResponse: (response) => extractErrorMessage(response),
      providesTags: ['Form'],
    }),

    getAssessment: builder.query<Assessment, { id: string; lang?: 'ar' | 'en' | 'ku' }>({
      query: ({ id, lang = 'ar' }) => {
        const odooLang = getOdooLang(lang);
        return {
          url: `/api/moys/assessments/${id}?lang=${odooLang}`,
          method: 'POST',
        };
      },
      transformResponse: (response: any) => {
        if (!response?.success || !response.data) {
          throw new Error('Invalid response format from assessment detail API');
        }
        return transformOdooAssessment(response.data as OdooAssessment);
      },
      transformErrorResponse: (response) => extractErrorMessage(response),
      providesTags: (_result, _err, arg) => [{ type: 'Form' as const, id: arg.id }],
    }),

    createUserToken: builder.mutation<
      { success: boolean; message: string; data: { id: number; token: string; user_name: string; user_email: string; user_phone: string } },
      { token: string; user_name: string; user_email?: string; user_phone: string }
    >({
      query: (body) => ({
        url: '/api/moys/user-token',
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: any) => {
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to create user token');
        }
        return response;
      },
      transformErrorResponse: (response) => extractErrorMessage(response),
    }),
  }),
});

export const { useGetAssessmentsQuery, useGetAssessmentQuery, useCreateUserTokenMutation } = assessmentsApi;
